/**
 * Canonical enum values shared by the API (Prisma), the web client and the
 * validation schemas. Prisma's generated enums are kept byte-identical to
 * these lists; `apps/api/test/unit/enum-parity.test.ts` fails the build if
 * the two ever drift.
 */

export const ROLES = ['ADMIN', 'MANAGER', 'SALES_REP', 'ACCOUNTANT', 'READ_ONLY'] as const;
export type Role = (typeof ROLES)[number];

/**
 * Where an expense is in its approval. Two different people sign it off, in
 * order — an accountant, then an administrator — and only then does it reach
 * the bank ledger.
 */
export const EXPENSE_APPROVAL_STATUSES = [
  'PENDING_ACCOUNTANT',
  'PENDING_ADMIN',
  'APPROVED',
  'REJECTED',
] as const;
export type ExpenseApprovalStatus = (typeof EXPENSE_APPROVAL_STATUSES)[number];

export const USER_STATUSES = ['INVITED', 'ACTIVE', 'DISABLED'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

/**
 * Lead status and source are *not* enums.
 *
 * Every business qualifies leads differently, and an enum means a schema
 * migration to add "Trade show". These are the values a new organisation
 * starts with; after that the list lives in a system picklist an admin edits
 * (see `system-fields.ts`), and the columns are plain strings validated
 * against it.
 *
 * `CONVERTED` is the one value code depends on, so it cannot be removed.
 */
export const DEFAULT_LEAD_STATUSES = [
  'NEW',
  'NOT_CONTACTED',
  'ATTEMPTED_TO_CONTACT',
  'CONTACTED',
  'CONTACT_IN_FUTURE',
  'PRE_QUALIFIED',
  'QUALIFIED',
  'NOT_QUALIFIED',
  'UNQUALIFIED',
  'LOST_LEAD',
  'JUNK_LEAD',
  // Last, and the one the app depends on: conversion sets it.
  'CONVERTED',
] as const;

/** How the statuses read, where capitalising the first letter would get it wrong. */
export const LEAD_STATUS_LABELS: Readonly<Record<string, string>> = {
  ATTEMPTED_TO_CONTACT: 'Attempted To Contact',
  CONTACT_IN_FUTURE: 'Contact In Future',
  JUNK_LEAD: 'Junk Lead',
  LOST_LEAD: 'Lost Lead',
  NOT_CONTACTED: 'Not Contacted',
  PRE_QUALIFIED: 'Pre-Qualified',
  NOT_QUALIFIED: 'Not Qualified',
};

export const DEFAULT_LEAD_SOURCES = [
  'NONE',
  'INSTAGRAM',
  'WHATSAPP',
  'EMPLOYEE_REFERRAL',
  'EXTERNAL_REFERRAL',
  'WEBSITE',
  'FACEBOOK',
  'EMAIL',
  'OTHER',
  'CROSS_VERTICAL_LEAD',
] as const;

/** Labels for the lead sources, in the order the dropdown lists them. */
export const LEAD_SOURCE_LABELS: Readonly<Record<string, string>> = {
  NONE: '-None-',
  INSTAGRAM: 'Instagram',
  WHATSAPP: 'Whatsapp',
  EMPLOYEE_REFERRAL: 'Employee Referral',
  EXTERNAL_REFERRAL: 'External Referral',
  WEBSITE: 'Website',
  FACEBOOK: 'Facebook',
  EMAIL: 'Email',
  OTHER: 'Other',
  CROSS_VERTICAL_LEAD: 'Cross Vertical Lead',
};

/** The states a lead's presence on one channel can be in. */
export const DIGITAL_FOOTPRINT_STATES = ['ACTIVE', 'PASSIVE', 'NOT_AVAILABLE'] as const;
export type DigitalFootprintState = (typeof DIGITAL_FOOTPRINT_STATES)[number];
export const DIGITAL_FOOTPRINT_STATE_LABELS: Readonly<Record<DigitalFootprintState, string>> = {
  ACTIVE: 'Active',
  PASSIVE: 'Passive',
  NOT_AVAILABLE: 'Not Available',
};
/** The channels, in display order. Stored under these keys in `digitalFootprint`. */
export const DIGITAL_FOOTPRINT_CHANNELS = [
  'website',
  'whatsapp',
  'instagram',
  'youtube',
  'googleMyBusiness',
] as const;
export type DigitalFootprintChannel = (typeof DIGITAL_FOOTPRINT_CHANNELS)[number];
export const DIGITAL_FOOTPRINT_CHANNEL_LABELS: Readonly<Record<DigitalFootprintChannel, string>> = {
  website: 'Website',
  whatsapp: 'Whatsapp',
  instagram: 'Instagram',
  youtube: 'Youtube',
  googleMyBusiness: 'Google My Business',
};

/**
 * What kind of business a lead is. The stored value is a stable key; the label
 * an administrator sees lives in `BUSINESS_CATEGORY_LABELS` and is theirs to
 * rename in Settings → Custom fields.
 */
export const DEFAULT_BUSINESS_CATEGORIES = [
  'CAR_RENTALS',
  'ENTERTAINERS',
  'CATERERS',
  'BRIDAL_ACCESSORIES',
  'RESORTS',
  'SALONS_AND_SPA',
  'WEDDING_PLANNERS',
  'CAKES_AND_CONFECTIONERY',
  'DJ',
  'DECORATORS',
  'SOUND_AND_LIGHTING',
  'CHOREOGRAPHERS',
  'VIDEOGRAPHERS',
  'CHOIRS',
  'PROFESSIONAL_SERVICES',
  'DESIGNERS',
  'ROCE_BANDS_WEDDING_TRADITIONS',
  'SALONS_AND_MAKEUP_ARTISTS',
  'RESTAURANTS',
  'GIFTS_AND_FAVOURS',
  'BANDS',
  'JEWELLERY',
  'VENUES',
  'TAILORS_AND_BOUTIQUES',
  'EMCEE',
  'FLORIST',
  'HONEYMOON_TRAVEL',
  'MEHENDI_ARTISTS',
  'DESIGNERS_AND_PRINTERS',
  'STAYS_AND_HOTELS',
  'BAR_SERVICES',
  'PHOTOGRAPHERS',
] as const;

/**
 * The market a record belongs to. Defaults for a new organisation; an admin
 * edits the list, and the column is a plain array of whatever they chose.
 *
 * The stored value is a stable key — renaming an option in the admin screen
 * changes only its label — so these stay SCREAMING_SNAKE_CASE like every
 * other system picklist even though the business's own four markets read as
 * mixed case. See `VERTICAL_DEFAULT_LABELS` in `system-fields.ts` for what
 * each one is actually called.
 */
export const DEFAULT_VERTICALS = [
  'PHOTOBOOTH_GOA',
  'DIGITAL_EXPERIENCES_GOA',
  'WEDDINGS_DE_GOA',
  'WHN_NEXT',
] as const;

/**
 * What a quote is for. Defaults for a new organisation; an admin edits the
 * list in the same screen as every other picklist.
 */
export const DEFAULT_SERVICE_TYPES = [
  'WEDDINGS_DE_GOA',
  'PHOTOBOOTH_SERVICES',
  'WHN_TECH_SERVICES',
  'SOCIAL_MEDIA_MANAGEMENT',
  'VIRTUAL_360_DE_GOA',
  'RESTAURANTS_DE_GOA',
  'WEDDINGS_DE_GOA_UK',
  'DIGITAL_EXPERIENCES_GOA',
  'OTHER',
] as const;

export const SERVICE_TYPE_LABELS: Readonly<Record<string, string>> = {
  WEDDINGS_DE_GOA: 'WeddingsdeGoa',
  PHOTOBOOTH_SERVICES: 'Photobooth Services',
  WHN_TECH_SERVICES: 'WHN Tech Services',
  SOCIAL_MEDIA_MANAGEMENT: 'Social Media Management',
  VIRTUAL_360_DE_GOA: 'Virtual 360 deGoa',
  RESTAURANTS_DE_GOA: 'RestaurantsdeGoa',
  WEDDINGS_DE_GOA_UK: 'WeddingsDeGoa UK',
  DIGITAL_EXPERIENCES_GOA: 'Digital Experiences Goa',
  OTHER: 'Other',
};

/**
 * Which of the organisation's own bank accounts a payment landed in.
 * Defaults for a new organisation; an admin edits the list to name their own
 * accounts, the same as every other system picklist.
 */
export const DEFAULT_DEPOSIT_ACCOUNTS = [
  'PRIMARY_BANK_ACCOUNT',
  'PAYMENT_GATEWAY',
  'PETTY_CASH',
] as const;

/**
 * What an expense was for. Defaults for a new organisation; an admin edits
 * the list in the same screen as every other system picklist.
 */
export const DEFAULT_EXPENSE_CATEGORIES = [
  'TRAVEL',
  'FUEL',
  'FOOD',
  'ACCOMMODATION',
  'OFFICE_SUPPLIES',
  'ADVERTISING',
  'SOFTWARE',
  'SUBSCRIPTIONS',
  'EQUIPMENT',
  'TRANSPORT',
  'CLIENT_ENTERTAINMENT',
  'STAFF_EXPENSES',
  'OTHER',
] as const;

/**
 * The currencies offered on a quote/invoice's currency selector.
 *
 * Not the only currencies the system accepts — `currencySchema` takes any
 * 3-letter ISO code, and an organisation's exchange rates can name any pair —
 * this is just what the dropdown shows without an admin having to type a
 * code. Add one here, with a rate on file, and it appears; nothing else needs
 * a deploy.
 */
export const SUPPORTED_DOCUMENT_CURRENCIES = ['INR', 'USD', 'CAD', 'EUR'] as const;
export type SupportedDocumentCurrency = (typeof SUPPORTED_DOCUMENT_CURRENCIES)[number];

export const DOCUMENT_CURRENCY_LABELS: Record<SupportedDocumentCurrency, string> = {
  INR: 'INR — Indian Rupee',
  USD: 'USD — US Dollar',
  CAD: 'CAD — Canadian Dollar',
  EUR: 'EUR — Euro',
};

/** The status a converted lead carries. Code reads it, so it is locked. */
export const CONVERTED_LEAD_STATUS = 'CONVERTED';

export const DEAL_STATUSES = ['OPEN', 'WON', 'LOST'] as const;
export type DealStatus = (typeof DEAL_STATUSES)[number];

/**
 * Why a deal was lost — a fixed list, not free text, so "why do deals fall
 * through" is a report, not a pile of one-off sentences to read by hand.
 */
export const DEAL_LOSS_REASONS = [
  'Price',
  'Expectation Mismatch',
  'Wrong Target',
  'Competition',
  'Unqualified Customer',
  'Lack of Response',
  'Missed Follow-ups',
  'Future Interest',
  'Other',
] as const;
export type DealLossReason = (typeof DEAL_LOSS_REASONS)[number];

/**
 * Three kinds, each with its own section in the app.
 *
 * `EVENT` is gone: an event and a meeting are the same shape — a start, an
 * end, a place and a guest list — and it only ever split one concept across
 * two menus.
 */
export const ACTIVITY_TYPES = ['TASK', 'MEETING', 'CALL'] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];

export const ACTIVITY_STATUSES = [
  'NOT_STARTED',
  'IN_PROGRESS',
  'COMPLETED',
  'DEFERRED',
  'CANCELLED',
] as const;
export type ActivityStatus = (typeof ACTIVITY_STATUSES)[number];

export const PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'] as const;
export type Priority = (typeof PRIORITIES)[number];

/** Modules that own records and can therefore carry custom fields. */
export const CRM_MODULES = [
  'LEAD',
  'ACCOUNT',
  'CONTACT',
  'DEAL',
  'ACTIVITY',
  'PRODUCT',
  'QUOTE',
  'VENDOR',
  'CAMPAIGN',
  'CASE',
  'SOLUTION',
  'SALES_ORDER',
  'INVOICE',
  'PAYMENT_RECEIVED',
  'EXPENSE',
] as const;
export type CrmModule = (typeof CRM_MODULES)[number];

/** Targets a note, attachment or activity can be attached to (polymorphic). */
export const RELATED_ENTITY_TYPES = ['LEAD', 'ACCOUNT', 'CONTACT', 'DEAL'] as const;
export type RelatedEntityType = (typeof RELATED_ENTITY_TYPES)[number];

export const CUSTOM_FIELD_TYPES = [
  'TEXT',
  'TEXTAREA',
  'NUMBER',
  'CURRENCY',
  'DATE',
  'DATETIME',
  'BOOLEAN',
  'PICKLIST',
  'MULTI_PICKLIST',
  'EMAIL',
  'PHONE',
  'URL',
  /** A reference to another record; the target is named by `lookupTarget`. */
  'LOOKUP',
  /** A single uploaded PDF file, stored outside the JSONB payload. */
  'PDF',
] as const;
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];

export const AUDIT_ACTIONS = [
  'CREATE',
  'UPDATE',
  'DELETE',
  'RESTORE',
  'CONVERT',
  'LOGIN',
  'LOGIN_FAILED',
  'LOGOUT',
  'EXPORT',
  'IMPORT',
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export const QUOTE_STATUSES = ['DRAFT', 'PRESENTED', 'ACCEPTED', 'REJECTED', 'EXPIRED'] as const;
export type QuoteStatus = (typeof QUOTE_STATUSES)[number];

// --- Zoho parity modules --------------------------------------------------
// Mirrored from schema.prisma; `enum-parity.test.ts` fails the build if either
// side drifts, because a value the web client offers and the database rejects
// is a 500 the user cannot act on.

export const CAMPAIGN_STATUSES = ['PLANNING', 'ACTIVE', 'INACTIVE', 'COMPLETE', 'ABORTED'] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

export const CAMPAIGN_TYPES = [
  'ADVERTISEMENT',
  'BANNER_ADS',
  'CONFERENCE',
  'DIRECT_MAIL',
  'EMAIL',
  'REFERRAL',
  'TELEMARKETING',
  'TRADE_SHOW',
  'WEBINAR',
  'OTHER',
] as const;
export type CampaignType = (typeof CAMPAIGN_TYPES)[number];

export const CASE_STATUSES = ['NEW', 'IN_PROGRESS', 'ESCALATED', 'ON_HOLD', 'CLOSED'] as const;
export type CaseStatus = (typeof CASE_STATUSES)[number];

export const CASE_PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'] as const;
export type CasePriority = (typeof CASE_PRIORITIES)[number];

export const CASE_ORIGINS = ['EMAIL', 'PHONE', 'WEB', 'CHAT', 'SOCIAL', 'OTHER'] as const;
export type CaseOrigin = (typeof CASE_ORIGINS)[number];

export const SOLUTION_STATUSES = ['DRAFT', 'REVIEW', 'APPROVED', 'OBSOLETE'] as const;
export type SolutionStatus = (typeof SOLUTION_STATUSES)[number];

export const SALES_ORDER_STATUSES = ['CREATED', 'APPROVED', 'DELIVERED', 'CANCELLED'] as const;
export type SalesOrderStatus = (typeof SALES_ORDER_STATUSES)[number];

/**
 * `PARTIALLY_PAID` is set by the payments module, never by hand: it means
 * money has arrived against this invoice but not all of it.
 *
 * `OVERDUE` is the odd one out. It is stored for the sake of a tenant that
 * sets it deliberately, but the API also *derives* it per request from the due
 * date (`InvoiceSummary.isOverdue`), because a stored overdue flag needs a job
 * to keep it true and the job is always either not running or running at the
 * wrong hour.
 */
export const INVOICE_STATUSES = [
  'DRAFT',
  'SENT',
  'PARTIALLY_PAID',
  'PAID',
  'OVERDUE',
  'CANCELLED',
] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

/**
 * `PARTIALLY_APPLIED`/`APPLIED` are set by `CreditNotesService`, never by
 * hand — the same reasoning as `InvoiceStatus.PARTIALLY_PAID`: they follow
 * from the sum of a credit note's `CreditNoteApplication` rows, not from
 * anyone choosing them directly. `DRAFT` and `ISSUED` are the two a person
 * does set, and `CANCELLED` is sticky once applied amounts are unwound.
 */
export const CREDIT_NOTE_STATUSES = [
  'DRAFT',
  'ISSUED',
  'PARTIALLY_APPLIED',
  'APPLIED',
  /** Consumed by cash refunds alone. */
  'REFUNDED',
  'CANCELLED',
] as const;
export type CreditNoteStatus = (typeof CREDIT_NOTE_STATUSES)[number];

export const PURCHASE_ORDER_STATUSES = [
  'DRAFT',
  'SENT',
  /** The vendor's quotation or invoice has come back and been recorded. */
  'VENDOR_RESPONDED',
  'PARTIALLY_RECEIVED',
  'COMPLETED',
  'CANCELLED',
] as const;
export type PurchaseOrderStatus = (typeof PURCHASE_ORDER_STATUSES)[number];

/**
 * Expense report workflow.
 *
 * "Submitted" and "Approved" are *events* in a report's history rather than
 * resting states: nobody acts between filing and the approver's queue, or
 * between approval and the cash officer's queue, so the report moves straight
 * on to Pending approval and Awaiting cash. Each is still recorded, with who
 * and when, in the report's history.
 */
export const EXPENSE_REPORT_STATUSES = [
  'DRAFT',
  'PENDING_APPROVAL',
  'REJECTED',
  'AWAITING_CASH',
  'CASH_GIVEN',
  'PENDING_VERIFICATION',
  'REIMBURSED',
  'CANCELLED',
] as const;
export type ExpenseReportStatus = (typeof EXPENSE_REPORT_STATUSES)[number];

export const EXPENSE_REPORT_DUTIES = ['ACCOUNTANT', 'APPROVER', 'CASH_DISBURSER'] as const;
export type ExpenseReportDuty = (typeof EXPENSE_REPORT_DUTIES)[number];

export const GST_INVOICE_STATUSES = ['DRAFT', 'SENT', 'PARTIALLY_PAID', 'PAID', 'VOID'] as const;
export type GstInvoiceStatus = (typeof GST_INVOICE_STATUSES)[number];

export const RECURRING_EXPENSE_FREQUENCIES = [
  'DAILY',
  'WEEKLY',
  'MONTHLY',
  'QUARTERLY',
  'YEARLY',
] as const;
export type RecurringExpenseFrequency = (typeof RECURRING_EXPENSE_FREQUENCIES)[number];

export const RECURRING_EXPENSE_STATUSES = ['ACTIVE', 'PAUSED', 'COMPLETED', 'CANCELLED'] as const;
export type RecurringExpenseStatus = (typeof RECURRING_EXPENSE_STATUSES)[number];

/** Whether an expense occurrence was posted by the scheduler or asked for directly. */
export const RECURRING_EXPENSE_TRIGGERS = ['SCHEDULED', 'MANUAL'] as const;
export type RecurringExpenseTrigger = (typeof RECURRING_EXPENSE_TRIGGERS)[number];

/**
 * How money arrived.
 *
 * India-first, because the default organisation currency is INR: UPI and
 * NEFT/RTGS are how most business-to-business payments actually land here, and
 * a list without them forces every payment into "Other".
 */
export const PAYMENT_METHODS = [
  'BANK_TRANSFER',
  'UPI',
  'CARD',
  'CHEQUE',
  'CASH',
  'NET_BANKING',
  'WALLET',
  'OTHER',
] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const DEAL_TYPES = ['NEW_BUSINESS', 'EXISTING_BUSINESS'] as const;
export type DealType = (typeof DEAL_TYPES)[number];

export const CALL_DIRECTIONS = ['INBOUND', 'OUTBOUND', 'MISSED'] as const;
export type CallDirection = (typeof CALL_DIRECTIONS)[number];

export const APPROVAL_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];

export const WORKFLOW_TRIGGER_TYPES = [
  'RECORD_CREATED',
  'RECORD_UPDATED',
  'FIELD_CHANGED',
  'RECORD_DELETED',
  'SCHEDULED',
] as const;
export type WorkflowTriggerType = (typeof WORKFLOW_TRIGGER_TYPES)[number];

export const WORKFLOW_ACTION_TYPES = [
  'UPDATE_FIELD',
  'CREATE_TASK',
  'SEND_EMAIL',
  'ASSIGN_BUSINESS_MANAGER',
  'NOTIFY_USER',
  'REQUEST_APPROVAL',
] as const;
export type WorkflowActionType = (typeof WORKFLOW_ACTION_TYPES)[number];

export const CONDITION_OPERATORS = [
  'EQUALS',
  'NOT_EQUALS',
  'CONTAINS',
  'NOT_CONTAINS',
  'STARTS_WITH',
  'ENDS_WITH',
  'GREATER_THAN',
  'GREATER_OR_EQUAL',
  'LESS_THAN',
  'LESS_OR_EQUAL',
  'IS_EMPTY',
  'IS_NOT_EMPTY',
  'IN',
  'NOT_IN',
  'CHANGED',
  /** A literal, inclusive [from, to] range — value is a two-element string array of ISO dates. */
  'BETWEEN',
  /**
   * A named, relative date window resolved at evaluation time, not when the
   * filter was saved — "this quarter" means the quarter containing whichever
   * day the filter actually runs on. Value is one of `RELATIVE_DATE_RANGE_TOKENS`.
   */
  'RELATIVE_DATE_RANGE',
] as const;
export type ConditionOperator = (typeof CONDITION_OPERATORS)[number];

export const ASSIGNMENT_STRATEGIES = ['ROUND_ROBIN', 'LOAD_BALANCED', 'TERRITORY'] as const;
export type AssignmentStrategy = (typeof ASSIGNMENT_STRATEGIES)[number];

export const EMAIL_DIRECTIONS = ['INBOUND', 'OUTBOUND'] as const;
export type EmailDirection = (typeof EMAIL_DIRECTIONS)[number];

export const NOTIFICATION_TYPES = [
  'MENTION',
  'ASSIGNMENT',
  'APPROVAL_REQUEST',
  'APPROVAL_DECISION',
  'TASK_DUE',
  'WORKFLOW',
  'SYSTEM',
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

/** How warm an account is. Fixed: the business uses exactly these three. */
export const ACCOUNT_RATINGS = ['HOT', 'WARM', 'COLD'] as const;
export type AccountRating = (typeof ACCOUNT_RATINGS)[number];
export const ACCOUNT_RATING_LABELS: Readonly<Record<AccountRating, string>> = {
  HOT: 'Hot',
  WARM: 'Warm',
  COLD: 'Cold',
};

/** Whether a quote is for a returning customer or a new one. Internal. */
export const QUOTE_CLIENT_TYPES = ['EXISTING', 'NEW'] as const;
export type QuoteClientType = (typeof QUOTE_CLIENT_TYPES)[number];
export const QUOTE_CLIENT_TYPE_LABELS: Readonly<Record<QuoteClientType, string>> = {
  EXISTING: 'Existing',
  NEW: 'New',
};

/** How big a client this looks like. Internal. */
export const QUOTE_HIGH_FLYER_STATUSES = ['YES', 'NO', 'PROBABLE'] as const;
export type QuoteHighFlyerStatus = (typeof QUOTE_HIGH_FLYER_STATUSES)[number];
export const QUOTE_HIGH_FLYER_LABELS: Readonly<Record<QuoteHighFlyerStatus, string>> = {
  YES: 'Yes (above 15k)',
  NO: 'No (below 10k)',
  PROBABLE: 'Probable (between 10k-14k)',
};

/** The service type whose quotes carry an event date, time and location. */
export const PHOTOBOOTH_SERVICE_TYPE = 'PHOTOBOOTH_SERVICES';
